/**
 * Self-contained garment mockup preview — no external API dependency (see the plan this was
 * built from: this business doesn't have a Printful account, so mockup rendering can't depend
 * on Printful's mockup-generator API). Draws a simple, generic tee silhouette and overlays the
 * customer's uploaded artwork in the print zone for the chosen placement.
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Print-zone rectangle (in the SVG's own 300x340 coordinate space) per placement.
export const PLACEMENT_ZONES = {
  front: { x: 105, y: 120, width: 90, height: 100, label: 'Front' },
  back: { x: 105, y: 120, width: 90, height: 100, label: 'Back' },
  left_sleeve: { x: 55, y: 110, width: 35, height: 35, label: 'Left Sleeve' },
  right_sleeve: { x: 210, y: 110, width: 35, height: 35, label: 'Right Sleeve' },
};

export default function GarmentMockup({ placement, artworkUrl, color = '#f0f0f0', garmentSource }) {
  const zone = PLACEMENT_ZONES[placement];
  if (!zone) return null;

  return (
    <div style={{ position: 'relative' }}>
      {garmentSource && (
        <span
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            fontSize: '10px',
            fontWeight: 700,
            padding: '2px 8px',
            borderRadius: '999px',
            background: garmentSource === 'customer_supplies' ? 'transparent' : 'var(--primary-color)',
            color: garmentSource === 'customer_supplies' ? 'var(--primary-color)' : '#fff',
            border: garmentSource === 'customer_supplies' ? '1px dashed var(--primary-color)' : 'none',
            zIndex: 1,
          }}
        >
          {garmentSource === 'customer_supplies' ? 'Your garment' : 'Our stock'}
        </span>
      )}
      <svg viewBox="0 0 300 340" style={{ width: '100%', maxWidth: '260px', display: 'block', margin: '0 auto' }}>
        {/* Generic tee silhouette — sleeves + body, schematic rather than photorealistic */}
        <path
          d="M 90 60 L 40 100 L 65 130 L 90 110 L 90 320 L 210 320 L 210 110 L 235 130 L 260 100 L 210 60 L 180 75 Q 150 90 120 75 Z"
          fill={color}
          stroke="#ccc"
          strokeWidth="2"
        />
        {/* Print-zone outline */}
        <rect
          x={zone.x}
          y={zone.y}
          width={zone.width}
          height={zone.height}
          fill="none"
          stroke="#999"
          strokeDasharray="4 3"
          strokeWidth="1.5"
        />
        <AnimatePresence>
          {artworkUrl ? (
            <motion.image
              key={artworkUrl}
              href={artworkUrl}
              x={zone.x}
              y={zone.y}
              width={zone.width}
              height={zone.height}
              preserveAspectRatio="xMidYMid meet"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              style={{ transformOrigin: `${zone.x + zone.width / 2}px ${zone.y + zone.height / 2}px` }}
            />
          ) : (
            <text
              x={zone.x + zone.width / 2}
              y={zone.y + zone.height / 2}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="10"
              fill="#aaa"
            >
              {zone.label}
            </text>
          )}
        </AnimatePresence>
      </svg>
    </div>
  );
}
